import type { AxiosRequestConfig } from 'axios';
import Axios from 'axios';
import type { BaseQueryFn } from '@reduxjs/toolkit/query/react';
import type { touchWebClient } from 'tradera/utils/http';

const getRequestConfig = (args: string | CustomAxiosRequestConfig) => {
    if (typeof args === 'string') {
        return { url: args };
    }

    const { body, ...config } = args;

    return {
        ...config,
        data: body ?? config.data,
    };
};

type HttpClientBaseQueryArgs = {
    client: typeof touchWebClient;
};

type CustomAxiosRequestConfig = AxiosRequestConfig & {
    body?: AxiosRequestConfig['data'];
};

/**
 * A base query function for use with `createApi` that uses our own http client(s) to make requests.
 *
 * @example
 * create a new api with this base query
 *
 * import { createApi } from '@reduxjs/toolkit/query/react';
 * import { touchWebClient } from 'tradera/utils/http';
 * import { httpClientBaseQuery } from './utils/http-client-base-query';
 *
 * export const touchwebSyiApi = createApi({
 *    baseQuery: httpClientBaseQuery({ client: touchWebClient }),
 *    ...etc... *
 *
 * @example
 * using the api
 *
 * 1. either with just a string
 * query: (params) => `/search.json?${qs.stringify(params)}`
 *
 * 2. or with a full config (this is basically an axios config but with the added body property which you can use for convenience, data works the same way as axios does)
 *     query: (parameters) => ({
                url: '/my/savedsearches/add',
                method: 'POST',
                body: parameters
            }),

        OR (these two will do the same thing)

            query: (parameters) => ({
                url: '/my/savedsearches/add',
                method: 'POST',
                data: parameters
            }),
 */
export function httpClientBaseQuery({
    client,
}: HttpClientBaseQueryArgs): BaseQueryFn<
    CustomAxiosRequestConfig | string,
    unknown,
    unknown
> {
    return async (args, api) => {
        try {
            const requestConfig = getRequestConfig(args);

            const result = await client.request({
                ...requestConfig,
                signal: api.signal,
            });

            return { data: result.data };
        } catch (error) {
            if (Axios.isAxiosError(error)) {
                return {
                    error: {
                        status: error.response?.status,
                        data: error.response?.data || error.message,
                    },
                };
            }

            return {
                error,
            };
        }
    };
}
